import { InlineIcon } from '@iconify/react';
import eyeOutline from '@iconify/icons-eva/eye-outline';
import eyeOff2Outline from '@iconify/icons-eva/eye-off-2-outline';
import cx from 'classnames';

interface Props {
  visible: boolean;
  onToggle: () => void;
  className?: string;
}

/**
 * A small eye / eye-off button used to reveal or hide the contents of an
 * adjacent password field. The parent is expected to be positioned (relative)
 * so the button can sit inside the field on its right-hand edge.
 *
 * The visibility state is owned by the parent and is intentionally not
 * persisted, so the password is always hidden again on the next visit.
 */
const PasswordVisibilityToggle = ({ visible, onToggle, className }: Props): JSX.Element => (
  <button
    type='button'
    onClick={onToggle}
    aria-label={visible ? 'Hide password' : 'Show password'}
    aria-pressed={visible}
    title={visible ? 'Hide password' : 'Show password'}
    className={cx(
      'absolute right-[1rem] top-1/2 -translate-y-1/2',
      // `!`-modifiers defend against broad legacy `button` rules in the surrounding
      // forms (e.g. the login form forces min-width/height on every button).
      'm-0 inline-flex !h-auto !min-w-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0',
      'text-[2rem] leading-none text-peeka-greyDark transition-colors hover:text-peeka-greyDarkest',
      'focus-visible:rounded-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-peeka-orange',
      className,
    )}
  >
    <InlineIcon icon={visible ? eyeOff2Outline : eyeOutline} />
  </button>
);

export default PasswordVisibilityToggle;
